import { supabase } from "@/integrations/supabase/client";
import { capture } from "@/lib/sentry/capture";
import { isMissingFunctionError } from "@/utils/postgrestErrors";
import { logger } from "@/utils/logger";
import {
  ContactAlreadyExistsError,
  ContactCreateDeniedError,
  ContactEditConflictError,
  ContactNotEditableError,
  ContactReadDeniedError,
  ContactWriteUnconfirmedError,
  ContactsUnavailableError,
  type ContactDraft,
  type ContactMethod,
  type ContactRow,
} from "./types";

interface PostgrestFailure {
  code?: string | null;
  message: string;
}

/** Missing table (PGRST205 / 42P01) or RPC: the migration has not applied. */
const MISSING_TABLE_CODES = new Set(["PGRST205", "42P01"]);

type ContactOperation = "fetch" | "create" | "update" | "delete";
type ContactContext = Record<string, number | null>;

function deniedContactOperation(operation: ContactOperation): Error {
  if (operation === "fetch") return new ContactReadDeniedError();
  return operation === "create" ? new ContactCreateDeniedError() : new ContactNotEditableError();
}

function fail(error: PostgrestFailure, operation: ContactOperation): never {
  if ((error.code && MISSING_TABLE_CODES.has(error.code)) || isMissingFunctionError(error)) {
    throw new ContactsUnavailableError();
  }
  // One entry per member per project: someone else saved theirs first.
  if (error.code === "23505") throw new ContactAlreadyExistsError();
  // save_contact: the row changed since the form loaded it.
  if (error.code === "40001" && operation === "update") throw new ContactEditConflictError();
  if (error.code === "42501") throw deniedContactOperation(operation);
  throw new Error("Contact request failed");
}

function safeFailure(error: unknown): Error {
  if (
    error instanceof ContactsUnavailableError ||
    error instanceof ContactAlreadyExistsError ||
    error instanceof ContactCreateDeniedError ||
    error instanceof ContactEditConflictError ||
    error instanceof ContactNotEditableError ||
    error instanceof ContactReadDeniedError ||
    error instanceof ContactWriteUnconfirmedError
  ) {
    return error;
  }
  return new Error("Contact request failed");
}

function isExpectedFailure(error: Error): boolean {
  return (
    error instanceof ContactsUnavailableError ||
    error instanceof ContactAlreadyExistsError ||
    error instanceof ContactCreateDeniedError ||
    error instanceof ContactEditConflictError ||
    error instanceof ContactNotEditableError ||
    error instanceof ContactReadDeniedError
  );
}

function reportFailure(
  operation: ContactOperation,
  error: unknown,
  context: ContactContext
): Error {
  const safe = safeFailure(error);
  const details = { operation, ...context };
  if (isExpectedFailure(safe)) {
    void logger.info("ui", `contacts.${operation}`, `Contacts ${operation} rejected`, {
      ...details,
      classification: safe.name,
    });
    return safe;
  }
  const sentryEventId = capture(safe, {
    tags: { feature: "contacts", action: operation },
    context: { contact_write: details },
    fingerprint: ["contacts", operation, safe.name],
  });
  void logger.error("ui", `contacts.${operation}`, `Contacts ${operation} failed`, safe, {
    ...details,
    sentry_event_id: sentryEventId,
  });
  return safe;
}

/** The RPC's row, snake_case as Postgres returns it. */
export interface ContactRpcRow {
  scope: string;
  project_id: number | null;
  project_name: string | null;
  contact_id: number | null;
  user_profile_id: number | null;
  first_name: string | null;
  last_name: string | null;
  account_email: string | null;
  avatar_url: string | null;
  color: string | null;
  full_name: string;
  company: string | null;
  job_title: string | null;
  phones: unknown;
  emails: unknown;
  notes: string | null;
  discipline_ids: unknown;
  is_org_admin: boolean;
  created_by: number | null;
  created_by_name: string | null;
  modified_at: string | null;
  has_access: boolean;
  can_edit: boolean;
}

/** jsonb is typed `unknown` on the way in: keep only well-formed entries. */
function toMethods(raw: unknown): ContactMethod[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item): ContactMethod[] => {
    if (!item || typeof item !== "object") return [];
    const { value, label } = item as { value?: unknown; label?: unknown };
    if (typeof value !== "string" || value.trim() === "") return [];
    return [{ value, label: typeof label === "string" && label !== "" ? label : null }];
  });
}

function toIds(raw: unknown): number[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(Number).filter((id) => Number.isInteger(id) && id > 0);
}

export function mapContactRow(row: ContactRpcRow): ContactRow {
  return {
    scope: row.scope === "building" ? "building" : "project",
    projectId: row.project_id,
    projectName: row.project_name,
    // bigint arrives as a number from PostgREST for ids this size.
    contactId: row.contact_id == null ? null : Number(row.contact_id),
    userProfileId: row.user_profile_id,
    firstName: row.first_name,
    lastName: row.last_name,
    accountEmail: row.account_email,
    avatarUrl: row.avatar_url,
    color: row.color,
    fullName: row.full_name,
    company: row.company,
    jobTitle: row.job_title,
    phones: toMethods(row.phones),
    emails: toMethods(row.emails),
    notes: row.notes,
    disciplineIds: toIds(row.discipline_ids),
    isOrgAdmin: row.is_org_admin === true,
    createdBy: row.created_by,
    createdByName: row.created_by_name,
    modifiedAt: row.modified_at,
    hasAccess: row.has_access,
    canEdit: row.can_edit,
  };
}

/**
 * Every contact the viewer may see in a building, or in one project of it.
 * `projectId` null is the Building view: every project the viewer holds
 * VIEW_CONTACTS on, archived and hidden projects excluded.
 */
export async function fetchContacts(
  buildingId: number,
  projectId: number | null
): Promise<ContactRow[]> {
  try {
    const { data, error } = await supabase.rpc("get_contacts", {
      p_building_id: buildingId,
      p_project_id: projectId,
    });
    if (error) fail(error, "fetch");
    return ((data ?? []) as ContactRpcRow[]).map(mapContactRow);
  } catch (error) {
    throw reportFailure("fetch", error, { building_id: buildingId, project_id: projectId });
  }
}

/** save_contact's arguments for everything the form edits. */
function fieldsFrom(draft: ContactDraft, isMember: boolean) {
  return {
    // A member's name follows their profile; the server ignores it for them.
    p_full_name: isMember ? null : draft.fullName,
    p_company: draft.company || null,
    p_job_title: draft.jobTitle || null,
    p_phones: draft.phones,
    p_emails: draft.emails,
    p_notes: draft.notes || null,
    p_discipline_ids: draft.disciplineIds,
  };
}

/** save_contact returns the entry id; anything else is an unconfirmed write. */
function assertSaved(data: unknown) {
  if (typeof data !== "number" && !(typeof data === "string" && /^\d+$/.test(data))) {
    throw new ContactWriteUnconfirmedError();
  }
}

export interface CreateContactInput {
  buildingId: number;
  /** The project, or null for an org owner/admin's building-wide entry. */
  projectId: number | null;
  /** Set for a project member; their name is taken from the profile. */
  userProfileId: number | null;
  draft: ContactDraft;
}

/**
 * Entry and responsibilities are saved together by `save_contact`, in one
 * transaction. Author, building and a member's name are stamped server-side.
 */
export async function createContact({
  buildingId,
  projectId,
  userProfileId,
  draft,
}: CreateContactInput) {
  try {
    const { data, error } = await supabase.rpc("save_contact", {
      p_contact_id: null,
      p_building_id: buildingId,
      p_project_id: projectId,
      p_user_profile_id: userProfileId,
      p_expected_modified_at: null,
      ...fieldsFrom(draft, userProfileId != null),
    });
    if (error) fail(error, "create");
    assertSaved(data);
  } catch (error) {
    throw reportFailure("create", error, {
      building_id: buildingId,
      project_id: projectId,
      user_profile_id: userProfileId,
    });
  }
}

/**
 * `expectedModifiedAt` is the version the form loaded (null = never edited).
 * The server refuses the save with 40001 if someone else saved in between.
 */
export async function updateContact(
  contactId: number,
  draft: ContactDraft,
  isMember: boolean,
  expectedModifiedAt: string | null
): Promise<void> {
  try {
    if (
      expectedModifiedAt !== null &&
      (typeof expectedModifiedAt !== "string" || expectedModifiedAt.trim() === "")
    ) {
      throw new ContactEditConflictError();
    }
    const { data, error } = await supabase.rpc("save_contact", {
      p_contact_id: contactId,
      p_building_id: null,
      p_project_id: null,
      p_user_profile_id: null,
      p_expected_modified_at: expectedModifiedAt,
      ...fieldsFrom(draft, isMember),
    });
    if (error) fail(error, "update");
    assertSaved(data);
  } catch (error) {
    throw reportFailure("update", error, { contact_id: contactId });
  }
}

/**
 * An RLS refusal on DELETE is not an error — it matches zero rows. Request
 * the affected-row count without RETURNING, which would need SELECT.
 */
function assertDeleted(count: number | null) {
  if (count === 0) throw new ContactNotEditableError();
  if (count !== 1) throw new ContactWriteUnconfirmedError();
}

export async function deleteContact(contactId: number): Promise<void> {
  try {
    const { count, error } = await supabase
      .from("project_contacts")
      .delete({ count: "exact" })
      .eq("id", contactId);
    if (error) fail(error, "delete");
    assertDeleted(count);
  } catch (error) {
    throw reportFailure("delete", error, { contact_id: contactId });
  }
}
